import { type ProductSimple } from '@/domain/products/types';
import { useCartStore } from '@/features/cart/store/cart';
import { Fragment, useMemo } from 'react';

import ProductCard from '../ProductCard';
import styles from './CardsList.module.scss';

type CardsListProps = {
  layout: string;
  filteredProducts: ProductSimple[];
};

interface Dictionary<TKey, TValues> {
  key: TKey;
  values: TValues[];
}

const groupByProductGroup = (data: ProductSimple[]): Array<Dictionary<string, ProductSimple>> =>
  data
    .reduce<Array<Dictionary<string, ProductSimple>>>((arr, curr) => {
      const index = arr.findIndex((e) => e.key === curr.group.productGroup);
      if (index >= 0) {
        arr[index].values.push(curr);
      } else {
        arr.push({ key: curr.group.productGroup, values: [curr] });
      }
      return arr;
    }, [])
    .sort((a, b) => (a.key < b.key ? -1 : 1));

const CardsList = ({ layout, filteredProducts }: CardsListProps) => {
  const cartProducts = useCartStore((s) => s.cartProducts);
  const cartProductIds = useMemo(() => new Set(cartProducts.map((p) => p.idProduct)), [cartProducts]);
  const groupedProducts = useMemo(() => groupByProductGroup(filteredProducts), [filteredProducts]);

  const renderCardList = useMemo(
    () =>
      groupedProducts.map(({ key, values }) => (
        <Fragment key={key}>
          <div className={styles.groupHeaderCard}>
            <h2>{key}</h2>
          </div>
          {values.map((product) => (
            <ProductCard key={product.idProduct} product={product} layout="large" isInCart={cartProductIds.has(product.idProduct)} />
          ))}
        </Fragment>
      )),
    [groupedProducts, cartProductIds],
  );

  const renderGridList = useMemo(
    () =>
      groupedProducts.map(({ key, values }) => (
        <Fragment key={key}>
          <div className={styles.groupHeaderCard}>
            <h2>{key}</h2>
          </div>
          {values.map((product) => (
            <ProductCard key={product.idProduct} product={product} layout="small" isInCart={cartProductIds.has(product.idProduct)} />
          ))}
        </Fragment>
      )),
    [groupedProducts, cartProductIds],
  );

  const renderProducts = useMemo(() => {
    if (groupedProducts.length === 0)
      return <div className={styles.emptyList}>Based on your selection criteria nothing has been found</div>;
    return layout === 'card' ? renderCardList : renderGridList;
  }, [groupedProducts.length, layout, renderCardList, renderGridList]);

  return renderProducts;
};

export default CardsList;
